import { defineStore } from "pinia";
import api from "@/api";
import { TrainingStatusEnum } from "@/constants/trainings.js";
import { useUserStore } from "./user";
import { useDepartmentStore } from "./department";
import { formatTime } from "@/utils/time"

function _getDefaultTraining(interaction, userId) {
  return {
    updated_query: interaction.query,
    Data: interaction.response,
    Status: TrainingStatusEnum.REVIEW,
    user_id_pv2: userId,
    department: interaction.department,
    Interaction: interaction._id,
    is_new: true,
  };
}

function processCitationsForTrainings(text, citations) {
  const titles = [];
  let citationIdx = 1;

  const citationsDict = citations.reduce((dict, citation) => {
    const emoji = citation.page ? "📄" : citation.timestamp ? "🎬" : "🌐";

    let pageInfo = "";
    if (citation.page) pageInfo = `on page(s) ${citation.page}`;
    else if (citation.timestamp) pageInfo = `at ${formatTime(citation.timestamp)}`;

    const parts = [
      `[${citationIdx}] ${emoji}`,
      citation.url || "",
      citation.name ? `– ${citation.name}` : "",
      pageInfo,
    ]
      .filter(Boolean)
      .join(" ");

    titles.push(parts);

    dict[citation.anchor] = { ...citation, index: citationIdx };
    citationIdx++;

    return dict;
  }, {});

  let found = false;

  const body = text.replace(/\[\[cite:(\d+)\]\]/g, match => {
    const citation = citationsDict[match];
    if (!citation) return "";
    found = true;
    return `[${citation.index}]`;
  });

  return found ? body + "\n\n" + titles.join("\n") : body;
}

export const useInteractionTrainingStore = defineStore("interactionTraining", {
  state: () => ({
    loading: false,

    interactions: {}, // { id: interactionObj }
    trainings: {},    // { id: trainingObj }

    activeInteractionId: null,
    isTrainingPopupOpen: false,
  }),

  getters: {
    activeInteraction(state) {
      return state.interactions[state.activeInteractionId] || null;
    },

    activeTraining(state) {
      return state.trainings[state.activeInteractionId] || null;
    },

    isUserApprover: (state) => (interactionId) => {
      const deptStore = useDepartmentStore();
      const userStore = useUserStore();

      const training = state.trainings[interactionId];
      if (!training) return false;

      const deptId = training.department;
      if (!deptId) return false;

      const approvers =
        deptStore.allDict[deptId]?.training_approvers_acc_ids || [];

      return approvers.includes(userStore.authUserId);
    },
  },
  actions: {
    async load(interactionId) {
      this.loading = true;

      try {
        const userStore = useUserStore();

        if (!this.interactions[interactionId]) {
          this.interactions[interactionId] =
            await api.interactions.getInteraction(interactionId);
        }

        const interaction = this.interactions[interactionId];

        let training =
          await api.trainings.getTrainingByInteractionId(interactionId);

        if (!training) {
          training = _getDefaultTraining(interaction, userStore.authUserId);
        }

        const md = training.Data || "";
        const citations = interaction.citations || [];

        training.Data = processCitationsForTrainings(md, citations);

        // Cache training
        this.trainings[interactionId] = training;

      } catch (err) {
        console.error("Error loading training:", err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    setActiveInteraction(interactionId, openPopup = true) {
      this.activeInteractionId = interactionId;
      this.isTrainingPopupOpen = openPopup;
    },
    resetActiveInteraction() {
      this.activeInteractionId = null;
      this.isTrainingPopupOpen = false;
    },
    async updateTraining() {
      const id = this.activeInteractionId;
      const training = this.trainings[id];
      if (!training) return;

      this.loading = true;

      try {
        if (training._id) {
          this.trainings[id] = await api.trainings.updateTraining(
            training._id,
            training
          );
        } else {
          this.trainings[id] = await api.trainings.createTraining(training);
        }
      } catch (err) {
        console.error("Error updating training:", err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    resetTraining() {
      const id = this.activeInteractionId;
      const interaction = this.interactions[id];
      if (!interaction) return;

      const userStore = useUserStore();
      this.trainings[id] = _getDefaultTraining(interaction, userStore.authUserId);
    },
  },
});
